/**
 * Live Chat Service - Default Implementation
 *
 * Manages chat connection lifecycle:
 * - Loads initial messages via RPC
 * - Listens for new messages via SSE
 * - Sends messages via RPC
 * - Handles reconnection on event change
 */

import { inject } from "../../di";
import type { IWritableLiveChatState, ILiveChatService, IChatMessage } from "./types";
import type { IRpcService } from "../rpc/types";
import type { ISseService } from "../sse/types";

const MAX_MESSAGES = 100;
const INITIAL_LOAD_LIMIT = 50;
const CHAT_USERNAME_KEY = "wow_chat_username";

export class LiveChatService implements ILiveChatService {
  @inject<IWritableLiveChatState>("writableLiveChatState")
  private chatState!: IWritableLiveChatState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private unsubscribeSse: (() => void) | null = null;

  async connect(liveEventId: string): Promise<void> {
    this.disconnect();

    this.chatState.currentEventId.set(liveEventId);
    this.chatState.connectionStatus.set("loading");
    this.chatState.errorMessage.set("");

    // Setup SSE listener FIRST to catch messages during RPC load
    this.setupSseListener(liveEventId);

    try {
      const result = await this.rpcService.call<{ messages: IChatMessage[] }>("get-live-chat-messages", {
        liveEventId,
        limit: String(INITIAL_LOAD_LIMIT),
      });

      const loadedMessages: IChatMessage[] = result.messages.map((m) => ({
        id: m.id,
        userId: m.userId,
        userName: m.userName,
        userAvatarUrl: m.userAvatarUrl,
        content: m.content,
        sentAt: m.sentAt,
      }));

      // Merge loaded messages with any received via SSE during loading
      this.mergeInitialMessages(loadedMessages);
      this.chatState.connectionStatus.set("connected");
    } catch (error) {
      this.chatState.connectionStatus.set("error");
      this.chatState.errorMessage.set(error instanceof Error ? error.message : "Failed to load chat");
    }
  }

  disconnect(): void {
    this.removeSseListener();
    this.chatState.messages.set([]);
    this.chatState.connectionStatus.set("idle");
    this.chatState.currentEventId.set(null);
    this.chatState.errorMessage.set("");
    this.chatState.isSending.set(false);
  }

  async sendMessage(content: string): Promise<boolean> {
    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return false;

    const trimmedContent = content.trim();
    if (!trimmedContent || trimmedContent.length > 500) return false;

    this.chatState.isSending.set(true);

    try {
      const storedUsername = localStorage.getItem(CHAT_USERNAME_KEY);
      const result = await this.rpcService.call<{ success: boolean }>("send-live-chat-message", {
        liveEventId: eventId,
        content: trimmedContent,
        ...(storedUsername && { userName: storedUsername }),
      });

      return result.success;
    } catch (error) {
      console.error("[LiveChat] Failed to send message:", error);
      return false;
    } finally {
      this.chatState.isSending.set(false);
    }
  }

  dispose(): void {
    this.disconnect();
  }

  private mergeInitialMessages(loadedMessages: IChatMessage[]): void {
    const currentMessages = this.chatState.messages.get();

    if (currentMessages.length === 0) {
      this.chatState.messages.set(loadedMessages);
      return;
    }

    const existingIds = new Set(currentMessages.map((m) => m.id));
    const newFromLoad = loadedMessages.filter((m) => !existingIds.has(m.id));

    // Combine: loaded (historical) + SSE messages (newer)
    const merged = [...newFromLoad, ...currentMessages];
    merged.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());

    const trimmed = merged.length > MAX_MESSAGES ? merged.slice(merged.length - MAX_MESSAGES) : merged;

    this.chatState.messages.set(trimmed);
  }

  private setupSseListener(liveEventId: string): void {
    this.unsubscribeSse = this.sseService.on<{
      messageId: string;
      liveEventId: string;
      userId: string;
      userName: string;
      userAvatarUrl: string | null;
      content: string;
      sentAt: string;
    }>("live-chat-message", (data) => {
      // Only process messages for current event
      if (data.liveEventId !== liveEventId) return;

      const newMessage: IChatMessage = {
        id: data.messageId,
        userId: data.userId,
        userName: data.userName,
        userAvatarUrl: data.userAvatarUrl,
        content: data.content,
        sentAt: data.sentAt,
      };

      const currentMessages = this.chatState.messages.get();

      // Deduplicate: check if message already exists
      if (currentMessages.some((m) => m.id === newMessage.id)) return;

      // Add new message and trim if over limit
      const updatedMessages = [...currentMessages, newMessage];
      if (updatedMessages.length > MAX_MESSAGES) {
        updatedMessages.shift(); // Remove oldest
      }

      this.chatState.messages.set(updatedMessages);
    });
  }

  private removeSseListener(): void {
    if (this.unsubscribeSse) {
      this.unsubscribeSse();
      this.unsubscribeSse = null;
    }
  }
}
